// #564 T3: Nachrichten zu einem Vorgang (eigene Rückfragen rechts, Antworten der Verwaltung links) mit
// Eingabe „Rückfrage stellen“. Mieter- und Eigentümerportal. Es wird nichts verschickt.
import { useCallback, useEffect, useState } from 'react';
import { ChatCircleDots, CircleNotch, PaperPlaneTilt } from '@phosphor-icons/react';
import type { PortalNachricht } from '../../lib/portal/nachrichtenApi';

const ZEIT = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
};

export default function NachrichtenVerlauf({ ticketId, laden, senden }: {
  ticketId: string;
  laden: (id: string) => Promise<PortalNachricht[]>;
  senden: (id: string, text: string) => Promise<void>;
}) {
  const [liste, setListe] = useState<PortalNachricht[] | null>(null);
  const [text, setText] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const neu = useCallback(async () => setListe(await laden(ticketId)), [laden, ticketId]);
  useEffect(() => { void neu(); }, [neu]);

  const absenden = async () => {
    if (text.trim().length < 2 || sendet) return;
    setSendet(true); setFehler(null);
    try { await senden(ticketId, text); setText(''); await neu(); } catch (e) { setFehler((e as Error).message); } finally { setSendet(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} aria-label="Nachrichten">
      <span className="mp-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ChatCircleDots size={14} weight="fill" /> Nachrichten</span>
      {liste === null && <CircleNotch size={18} weight="bold" className="animate-spin" style={{ color: 'var(--p-w40)' }} />}
      {liste?.length === 0 && <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w45)' }}>Noch keine Nachrichten. Fragen Sie hier nach, die Verwaltung antwortet im Portal.</span>}
      {liste?.map((n) => (
        <div key={n.id} style={{ alignSelf: n.von_mir ? 'flex-end' : 'flex-start', maxWidth: '85%', display: 'flex', flexDirection: 'column', gap: 3,
          padding: '9px 12px', borderRadius: 16, background: n.von_mir ? 'var(--p-gold-fl2)' : 'var(--p-w08)' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: n.von_mir ? 'var(--p-gold-text)' : 'var(--p-w50)' }}>{n.von_mir ? 'Sie' : 'Verwaltung'} · {ZEIT(n.created_at)}</span>
          <span style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.45, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{n.inhalt}</span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} aria-label="Rückfrage an die Verwaltung"
          placeholder="Rückfrage an die Verwaltung …"
          style={{ flex: 1, minWidth: 0, resize: 'vertical', borderRadius: 14, padding: '10px 12px', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
            color: 'var(--p-w)', background: 'var(--p-w05)', border: 'none', boxShadow: 'inset 0 0 0 1px var(--p-w08)' }} />
        <button type="button" className="p-knopf p-knopf-gold" onClick={() => void absenden()} disabled={sendet || text.trim().length < 2}
          data-tip="Rückfrage an die Verwaltung senden" aria-label="Rückfrage senden" style={{ height: 44, width: 44, padding: 0, borderRadius: 22 }}>
          {sendet ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={17} weight="fill" />}
        </button>
      </div>
      {fehler && <span role="alert" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-fehler-text)' }}>{fehler}</span>}
    </div>
  );
}
